import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, tokenManager } from './api/auth';
import ECodeStep from './ECodeStep';
import OTPStep from './OTPStep';

const Login = () => {
  const [currentStep, setCurrentStep] = useState('ecode');
  const [sessionId, setSessionId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [ecode, setEcode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const [demoError, setDemoError] = useState(null);
  const navigate = useNavigate();

  const handleDemoSendCode = async () => {
    try {
      await authApi.demoLogin();
      navigate('/dashboard');
    } catch (error) {
      setDemoError(error.message);
    }
  };

  useEffect(() => {
    if (isDemoMode) return;
    const checkAuth = async () => {
      if (tokenManager.isAuthenticated()) {
        try {
          await authApi.getCurrentUser();
          navigate('/dashboard');
        } catch (error) {
          tokenManager.removeToken();
        }
      }
    };
    checkAuth();
  }, [isDemoMode, navigate]);

  useEffect(() => {
    setError(null);
  }, [currentStep]);

  const handleECodeSubmit = async (ecodeValue) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authApi.sendOTP(ecodeValue);
      const phoneMatch = response.message.match(/(\d{4})$/);
      const extractedPhone = phoneMatch ? phoneMatch[1] : null;
      setSessionId(response.session_id);
      setPhoneNumber(extractedPhone);
      setEcode(ecodeValue);
      setCurrentStep('otp');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerify = async (otpValue) => {
    setIsLoading(true);
    setError(null);
    try {
      await authApi.verifyOTP(sessionId, otpValue);
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToECode = () => {
    setCurrentStep('ecode');
    setSessionId(null);
    setPhoneNumber(null);
    setError(null);
  };

  const handleResendOTP = async (ecodeValue) => {
    setError(null);
    try {
      const response = await authApi.sendOTP(ecodeValue);
      setSessionId(response.session_id);
      const originalError = error;
      setError('New OTP sent successfully');
      setTimeout(() => {
        setError(originalError);
      }, 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  if (isDemoMode) {
    if (demoError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#f8fafc',
          fontFamily: 'sans-serif', textAlign: 'center', padding: 24,
        }}>
          <p style={{ color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>Could not start the demo</p>
          <p style={{ color: '#6b7280', fontSize: 14 }}>{demoError}</p>
        </div>
      );
    }
    return <ECodeStep onNext={handleDemoSendCode} isLoading={isLoading} error={error} />;
  }

  if (currentStep === 'ecode') {
    return (
      <ECodeStep
        onNext={handleECodeSubmit}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  if (currentStep === 'otp') {
    return (
      <OTPStep
        onVerify={handleOTPVerify}
        onBack={handleBackToECode}
        onResendOTP={handleResendOTP}
        isLoading={isLoading}
        error={error}
        phoneNumber={phoneNumber}
        ecode={ecode}
      />
    );
  }

  return null;
};

export default Login;
